import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CheckCircle2, LucideAngularModule, X } from 'lucide-angular';
import { filter } from 'rxjs';
import { AuthService } from './services/auth.service';
import { Header } from './shared/components/header/header';
import { Sidebar } from './shared/components/sidebar/sidebar';
import { Tour } from './shared/components/tour/tour';
import {
  CurrentUser,
  NotificationItem,
  SidebarView,
  UserRole,
} from './shared/models/layout.models';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Header, Sidebar, LucideAngularModule, Tour],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly authService = inject(AuthService);
  // private waitlistResetTimer: ReturnType<typeof setTimeout> | null = null; // TODO: Lista de espera será desenvolvido em outro momento

  private readonly routeByView: Record<SidebarView, string> = {
    dashboard: '/dashboard',
    wallet: '/wallet',
    calendar: '/calendar',
    reports: '/reports',
    setup: '/setup',
  };

  readonly role = signal<UserRole>('executive');
  readonly activeView = signal<SidebarView>('dashboard');
  // readonly waitlistStatus = signal<'idle' | 'success'>('idle'); // TODO: Lista de espera será desenvolvido em outro momento
  // readonly showToast = signal(false); // TODO: Lista de espera será desenvolvido em outro momento

  readonly notifications = signal<NotificationItem[]>([
    {
      id: 'n1',
      message: 'Hotel Solar atualizou disponibilidade para o evento corporativo de sexta-feira.',
      date: '2026-03-11T09:45:00-03:00',
      isRead: false,
    },
    {
      id: 'n2',
      message: 'Restaurante Terra confirmou menu especial para o grupo premium.',
      date: '2026-03-11T10:15:00-03:00',
      isRead: false,
    },
    {
      id: 'n3',
      message: 'Nova meta mensal publicada para a carteira de experiências corporativas.',
      date: '2026-03-11T11:30:00-03:00',
      isRead: true,
    },
  ]);

  readonly currentUser = computed<CurrentUser>(() => {
    const authenticatedUser = this.authService.currentUser();

    return {
      name: authenticatedUser?.name?.trim() || 'Usuário EVNT',
      type: this.role() === 'manager' ? 'manager' : 'executive',
    };
  });

  readonly icons = {
    checkCircle2: CheckCircle2,
    x: X,
  };

  constructor() {
    this.syncActiveView(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.syncActiveView(event.urlAfterRedirects);
      });

    // TODO: Lista de espera será desenvolvido em outro momento
    // this.destroyRef.onDestroy(() => {
    //   this.clearWaitlistTimer();
    // });
  }

  changeView(view: SidebarView): void {
    this.activeView.set(view);
    void this.router.navigate([this.routeByView[view]]);
  }

  toggleRole(role: UserRole): void {
    this.role.set(role);
  }

  logout(): void {
    this.authService.logout();
    void this.router.navigate(['/login']);
  }

  // TODO: Lista de espera será desenvolvido em outro momento
  // requestWaitlist(): void {
  //   if (this.waitlistStatus() === 'success') {
  //     return;
  //   }

  //   this.waitlistStatus.set('success');
  //   this.showToast.set(true);

  //   this.clearWaitlistTimer();
  //   this.waitlistResetTimer = setTimeout(() => {
  //     this.showToast.set(false);
  //     this.waitlistStatus.set('idle');
  //     this.waitlistResetTimer = null;
  //   }, 5000);
  // }

  // TODO: Lista de espera será desenvolvido em outro momento
  // dismissToast(): void {
  //   this.showToast.set(false);
  // }

  markNotificationAsRead(id: string): void {
    this.notifications.update((items) =>
      items.map((item) => (item.id === id ? { ...item, isRead: true } : item)),
    );
  }

  private syncActiveView(url: string): void {
    const cleanUrl = url.split('?')[0].split('#')[0];
    const segment = cleanUrl.split('/').filter(Boolean)[0] as SidebarView | undefined;

    if (segment && Object.hasOwn(this.routeByView, segment)) {
      this.activeView.set(segment);
    }
  }

  // TODO: Lista de espera será desenvolvido em outro momento
  // private clearWaitlistTimer(): void {
  //   if (this.waitlistResetTimer) {
  //     clearTimeout(this.waitlistResetTimer);
  //     this.waitlistResetTimer = null;
  //   }
  // }
}
